<template>
    <b-navbar class="editor-submenu bg-white" :disabled="!active">
        <b-nav>
            <b-btn-group v-if="!noHistory">
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'undo')" :title="'Undo|Annuler'|tr">
                    <i class="fas fa-undo fa-fw"/>
                </b-btn>
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'redo')" :title="'Redo|Refaire'|tr">
                    <i class="fas fa-redo fa-fw"/>
                </b-btn>
            </b-btn-group>

            <b-btn-group>
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'bold')" :title="'Bold|Gras'|tr">
                    <i class="fas fa-bold fa-fw"/>
                </b-btn>
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'italic')" :title="'Italic|Italique'|tr">
                    <i class="fas fa-italic fa-fw"/>
                </b-btn>
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'underline')" :title="'Underline|Souligner'|tr">
                    <i class="fas fa-underline fa-fw"/>
                </b-btn>

                <template v-if="!noAdvancedText">
                    <template v-if="isExtraLargeLayout">
                        <b-btn variant="white" size="sm" @click="onExecCmd($event, 'strikeThrough')" :title="'Strike Through|Barrer'|tr">
                            <i class="fas fa-strikethrough fa-fw"/>
                        </b-btn>
                        <b-btn variant="white" size="sm" @click="onExecCmd($event, 'subscript')" :title="'Subscript|Indice'|tr">
                            <i class="fas fa-subscript fa-fw"/>
                        </b-btn>
                        <b-btn variant="white" size="sm" @click="onExecCmd($event, 'superscript')" :title="'Superscript|Exposant'|tr">
                            <i class="fas fa-superscript fa-fw"/>
                        </b-btn>
                    </template>

                    <b-dropdown variant="white" class="icons-only" v-else>
                        <b-dropdown-item @click="onExecCmd($event, 'strikeThrough')" :title="tr(`Strike Through|Barrer`)">
                            <i class="fas fa-strikethrough fa-fw"/>
                        </b-dropdown-item>
                        <b-dropdown-item @click="onExecCmd($event, 'subscript')" :title="tr(`Subscript|Indice`)">
                            <i class="fas fa-subscript fa-fw"/>
                        </b-dropdown-item>
                        <b-dropdown-item @click="onExecCmd($event, 'superscript')" :title="tr(`Superscript|Exposant`)">
                            <i class="fas fa-superscript fa-fw"/>
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </b-btn-group>

            <b-btn-group v-if="!noColor || !noBackColor || !noFontSize">
                <input-color v-if="!noColor" size="sm" auto-hide @click="openColorPicker" @change="applyColor('foreColor', $event)"
                             force-color-value no-live-preview v-model="color" :title="'Text Color|Couleur du texte'|tr">
                    <i class="fas fa-font"/>
                </input-color>
                <input-color v-if="!noBackColor" class="ml-2" size="sm" @click="openColorPicker" auto-hide @change="applyColor('backColor', $event)"
                             force-color-value no-live-preview value="#eeeeee" :title="'Background Color|Couleur du fond'|tr">
                    <i class="fas fa-highlighter-line"/>
                </input-color>

                <b-select v-if="!noFontSize" size="sm" class="ml-2" v-model="fontSize" @input="setFontSize">
                    <option selected disabled value="default" class="text-italic" v-tr>Font Size|Taille de police</option>
                    <option value="reset" v-tr>Default size|Taille par défaut</option>
                    <optgroup :label="tr(`Responsive Size|Taille responsive`)">
                        <!-- 1 is reserved -->
                        <option :value="2" v-tr>X-Small|Très petit</option>
                        <option :value="3" v-tr>Smaller|Plus petit</option>
                        <option :value="4" v-tr>Normal</option>
                        <option :value="5" v-tr>Larger|Plus grand</option>
                        <option :value="6" v-tr>X-Large|Encore plus grand</option>
                        <option :value="7" v-tr>Biggest|Très grand</option>
                    </optgroup>
                    <optgroup :label="tr(`Fixed Size|Taille fixe`)">
                        <option v-for="n in 100" :value="`${n + 8}pt`">{{n + 8}}pt</option>
                    </optgroup>
                </b-select>
            </b-btn-group>

            <b-btn-group>
                <template v-if="isExtraLargeLayout">
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'justifyLeft')" :title="'Align Left|Aligner à gauche'|tr">
                        <i class="fas fa-align-left fa-fw"/>
                    </b-btn>
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'justifyCenter')" :title="'Center|Aligner au centre'|tr">
                        <i class="fas fa-align-center fa-fw"/>
                    </b-btn>
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'justifyRight')" :title="'Align Right|Aligner à droite'|tr">
                        <i class="fas fa-align-right fa-fw"/>
                    </b-btn>
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'justifyFull')" :title="'Justify|Justifier'|tr">
                        <i class="fas fa-align-justify fa-fw"/>
                    </b-btn>

                    <template v-if="!noIndent">
                        <b-btn variant="white" size="sm" @click="onExecCmd($event, 'indent')" :title="'Increase Indent|Augmenter indentation'|tr">
                            <i class="fas fa-indent fa-fw"/>
                        </b-btn>
                        <b-btn variant="white" size="sm" @click="onExecCmd($event, 'outdent')" :title="'Decrease Indent|Réduire indentation'|tr">
                            <i class="fas fa-outdent fa-fw"/>
                        </b-btn>
                    </template>
                </template>

                <b-dropdown variant="white" class="icons-only" v-else>
                    <template #button-content>
                        <i class="fas fa-align-center"/>
                    </template>
                    <b-dropdown-item @click="onExecCmd($event, 'justifyLeft')" :title="tr(`Align Left|Aligner à gauche`)">
                        <i class="fas fa-align-left fa-fw"/>
                    </b-dropdown-item>
                    <b-dropdown-item @click="onExecCmd($event, 'justifyCenter')" :title="tr(`Center|Aligner au centre`)">
                        <i class="fas fa-align-center fa-fw"/>
                    </b-dropdown-item>
                    <b-dropdown-item @click="onExecCmd($event, 'justifyRight')" :title="tr(`Align Right|Aligner à droite`)">
                        <i class="fas fa-align-right fa-fw"/>
                    </b-dropdown-item>
                    <b-dropdown-item @click="onExecCmd($event, 'justifyFull')" :title="tr(`Justify|Justifier`)">
                        <i class="fas fa-align-justify fa-fw"/>
                    </b-dropdown-item>

                    <template v-if="!noIndent">
                        <b-dropdown-item @click="onExecCmd($event, 'indent')" :title="tr(`Increase Indent|Augmenter indentation`)">
                            <i class="fas fa-indent fa-fw"/>
                        </b-dropdown-item>
                        <b-dropdown-item @click="onExecCmd($event, 'outdent')" :title="tr(`Decrease Indent|Réduire indentation`)">
                            <i class="fas fa-outdent fa-fw"/>
                        </b-dropdown-item>
                    </template>

                    <b-dropdown-divider/>

                    <template v-if="!noList">
                        <b-dropdown-item @click="onExecCmd($event, 'insertOrderedList')" :title="tr(`Ordered List|Liste ordonnée`)">
                            <i class="fas fa-list-ol fa-fw"/>
                        </b-dropdown-item>
                        <b-dropdown-item @click="onExecCmd($event, 'insertUnorderedList')" :title="tr(`Unordered List|Liste non ordonnée`)">
                            <i class="fas fa-list-ul fa-fw"/>
                        </b-dropdown-item>
                    </template>
                    <b-dropdown-item v-if="!noSeparator" @click="onExecCmd($event, 'insertHorizontalRule')" :title="tr(`Horizontal Rule|Séparateur horizontal`)">
                        <i class="fas fa-horizontal-rule fa-fw"/>
                    </b-dropdown-item>
                    <template v-if="!noLink">
                        <b-dropdown-item @click="openLinkModal" :title="tr(`Insert Link|Insérer un lien`)">
                            <i class="fas fa-link fa-fw"/>
                        </b-dropdown-item>
                        <b-dropdown-item @click="onExecCmd($event, 'unlink')" :title="tr(`Remove Link|Retirer un lien`)">
                            <i class="fas fa-link-slash fa-fw"/>
                        </b-dropdown-item>
                    </template>
                </b-dropdown>
            </b-btn-group>

            <b-btn-group v-if="isExtraLargeLayout">
                <template v-if="!noList">
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'insertOrderedList')" :title="'Ordered List|Liste ordonnée'|tr">
                        <i class="fas fa-list-ol fa-fw"/>
                    </b-btn>
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'insertUnorderedList')" :title="'Unordered List|Liste non ordonnée'|tr">
                        <i class="fas fa-list-ul fa-fw"/>
                    </b-btn>
                </template>
                <b-btn v-if="!noSeparator" variant="white" size="sm" @click="onExecCmd($event, 'insertHorizontalRule')" :title="'Horizontal Rule|Séparateur horizontal'|tr">
                    <i class="fas fa-horizontal-rule fa-fw"/>
                </b-btn>

                <template v-if="!noLink">
                    <b-btn variant="white" size="sm" @click="openLinkModal" :title="'Insert Link|Insérer un lien'|tr">
                        <i class="fas fa-link fa-fw"/>
                    </b-btn>
                    <b-btn variant="white" size="sm" @click="onExecCmd($event, 'unlink')" :title="'Remove Link|Retirer un lien'|tr">
                        <i class="fas fa-link-slash fa-fw"/>
                    </b-btn>
                </template>
            </b-btn-group>

            <b-btn-group>
                <b-btn variant="white" size="sm" @click="onExecCmd($event, 'removeFormat')" :title="'Remove Format|Enlever le format'|tr">
                    <i class="fas fa-pencil-slash fa-fw"/>
                </b-btn>
            </b-btn-group>

            <slot></slot>
        </b-nav>

        <b-modal v-model="linkModal.visible" size="lg" :title="tr(`Add a link|Ajouter un lien`)">
            <b-form-group :label="tr(`URL to insert|URL à insérer`)">
                <b-input-group>
                    <b-input-group-text><i class="fas fa-globe"/></b-input-group-text>
                    <e-input type="url" v-model="linkModal.url" required @enter="insertLink"/>
                </b-input-group>
            </b-form-group>
            <template #modal-footer>
                <b-btn @click="linkModal.visible = false" variant="default"><span v-tr>Cancel|Annuler</span></b-btn>
                <b-btn @click="insertLink" variant="primary"><span v-tr>Insert Link|Insérer un lien</span></b-btn>
            </template>
        </b-modal>
    </b-navbar>
</template>

<script>
import InputColor from "@/components/editor/properties/inputs/input-color.vue";
import EColorPicker from "@vue-components/components/e-color-picker.vue";
import EPhotoshopColorPicker from "@vue-components/components/color-picker/e-photoshop-color-picker.vue";
import InputColorPreview from "@components/editor/properties/inputs/input-color-preview.vue";
import EInput from "@vue-components/components/e-input.vue";
import Validation from "@vue-components/helpers/Validation.js";

// editor-submenu #hot-reload-debug
export default {
    name: `editor-submenu`,
    components: {EInput, InputColorPreview, EPhotoshopColorPicker, InputColor, EColorPicker},
    props: {
        disabled: {type: Boolean},
        noHistory: {type: Boolean}, // undo, redo
        noColor: {type: Boolean}, // fore color
        noBackColor: {type: Boolean}, // back color
        noLink: {type: Boolean},
        noList: {type: Boolean},
        noSeparator: {type: Boolean},
        noIndent: {type: Boolean}, // indent, outdent
        noFontSize: {type: Boolean},
        noAdvancedText: {type: Boolean}, // subscript, superscript, strikethrough
    },
    data() {
        return {
            Validation,
            previouslyFocus: null,
            lastSelection: null,
            lastRange: null,
            rangyBookmarks: null,
            selection: null,
            fontSize: `default`,
            active: false,
            disableSelection: false,
            linkModal: {
                visible: false,
                url: null
            },
            color: `#000000`,
            highlightColor: `transparent`
        }
    },
    created() {
        document.addEventListener(`selectionchange`, this.updateSelection);
    },
    beforeDestroy() {
        document.removeEventListener(`selectionchange`, this.updateSelection);
    },
    methods: {
        updateSelection() {
            if (this.disableSelection)
                return;

            this.selection = document.getSelection();
            this.fontSize = `default`;

            if (this.selection && this.selection.anchorNode) { // make sure it doesn't error if nothing is selected
                this.active = true;
                const style = window.getComputedStyle(this.selection.anchorNode.parentElement, null);
                // Returns size in PX not compatible with pt
                // this.fontSize = style.getPropertyValue(`font-size`) || `default`;
                this.color = style.getPropertyValue(`color`);
            } else {
                this.active = false;
            }
        },
        onExecCmd(event, cmd, value) {
            event.preventDefault();
            this.execCmd(cmd, value);
        },
        saveSelection() {
            // before losing focus (insert link/color picker) save current selection
            this.disableSelection = true;
            // prevent Vue from refreshing the text when editablecontent will throw blur event (it's delayed with a setTimeout)
            window.disableTextBlur = true;

            const selection = window.getSelection();
            if (selection.rangeCount) {
                this.lastSelection = selection.getRangeAt(0).cloneRange();
            }
        },
        restoreSelection() {
            if (!this.lastSelection)
                return false;

            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(this.lastSelection);

            delete window.disableTextBlur;

            if (this.lastSelection.collapsed)
                return false;

            return selection;
        },
        openColorPicker() {
            this.saveSelection();
        },
        applyColor(colorType = `foreColor`, color) {
            this.restoreSelection();

            this.$nextTick(() => {
                document.execCommand(colorType, false, color);
                this.disableSelection = false;
                this.updateSelection();

                // to let user visualize changes (remove if you need to add again live preview)
                window.getSelection().removeAllRanges();
                this.lastRange = null;
            });
        },
        openLinkModal() {
            this.saveSelection();
            this.linkModal.visible = true;
        },
        insertLink() {
            this.linkModal.visible = false;
            this.restoreSelection();

            this.$nextTick(() => {
                document.execCommand(`createLink`, false, this.linkModal.url);
            });
        },
        setFontSize(fontSize) {
            if (fontSize === `reset`) {
                document.execCommand(`fontSize`, false, `1`);

                // replace <font> generated with span without any style
                document.querySelectorAll(`font[size="1"]`).forEach(font => {
                    const span = document.createElement(`span`);
                    span.innerHTML = font.innerHTML;
                    font.replaceWith(span);
                });
            } else if (typeof fontSize === `string` && fontSize.indexOf(`pt`) > 0) {
                document.execCommand(`fontSize`, false, `1`);

                // replace <font> generated with styled span
                document.querySelectorAll(`font[size="1"]`).forEach(font => {
                    const span = document.createElement(`span`);
                    span.style.fontSize = fontSize;
                    span.innerHTML = font.innerHTML;
                    font.replaceWith(span);
                });
            } else {
                // execute font size, can sometimes create weird effects
                document.execCommand(`fontSize`, false, fontSize);
            }
        },
        execCmd(cmd, value) {
            document.execCommand(cmd, false, value);
            this.updateSelection();
        }
    }
}
</script>

<style lang="scss" scoped>
.editor-submenu {
    padding: 4px 4px;
    height: 44px;
    user-select: none;
    border-bottom: 1px solid #ccc;

    .btn-group:not(.dropdown) {
        padding-inline: 5px;

        .btn {
            border-radius: var(--border-radius);

            &.btn-white {
                color: #333 !important;

                &:hover {
                    color: #000 !important;
                }
            }
        }

        & + .btn-group {
            border-left: 1px solid rgba(black, 0.1);
        }
    }

    :deep(.input-color-preview.size-sm) {
        height: 31px;
        width: 31px;
    }
}
</style>
